import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Animated,
  ActivityIndicator,
  Dimensions,
  ScrollView,
  Image,
  StatusBar,
  Platform,
  TextInput,
  AppState,
  Alert
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import HoneyPotAPI from '../../services/honeypot';
import { H2, Body1, Body2, Caption } from '../../components/common/TextStyles';
import ConfettiCannon from 'react-native-confetti-cannon';

const { width } = Dimensions.get('window');
const MASCOT = require('../../assets/badger_mascot.png');

const HoneyPotScreen = () => {
  const navigation = useNavigation();
  const [challenge, setChallenge] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState('intro');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [currentTextAnswer, setCurrentTextAnswer] = useState('');
  const [results, setResults] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [pastStats, setPastStats] = useState({ totalWon: 0, count: 0 });

  const badgerBounce = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(0)).current;
  const startTimeRef = useRef(null);

  useEffect(() => {
    fetchActiveChallenge();
    startAnimations();
  }, []);

  useEffect(() => {
    let timer;
    if (step === 'playing' && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) { handleTimeUp(); return 0; }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [step, timeLeft]);

  useEffect(() => {
    const handleNavigationAway = async () => {
      if (step === 'playing' && attempt && !submitting) {
        setSubmitting(true);
        try {
          const res = await HoneyPotAPI.disqualifyAttempt(attempt.id);
          setResults({ attempt: res.attempt, pointsEarned: 0 });
          setStep('results');
        } catch (err) {
          console.error('Disqualify error', err);
        } finally {
          setSubmitting(false);
        }
      }
    };

    const unsubscribeApp = AppState.addEventListener('change', nextAppState => {
      if (nextAppState.match(/inactive|background/)) handleNavigationAway();
    });

    const unsubscribeNav = navigation.addListener('beforeRemove', (e) => {
      if (step === 'playing' && !submitting) {
        e.preventDefault();
        Alert.alert(
          'Disqualification Warning',
          'Leaving the quiz will automatically disqualify you with a score of 0. Are you sure?',
          [
            { text: 'Cancel', style: 'cancel', onPress: () => { } },
            {
              text: 'Leave',
              style: 'destructive',
              onPress: () => {
                handleNavigationAway().then(() => navigation.dispatch(e.data.action));
              }
            }
          ]
        );
      }
    });

    return () => {
      unsubscribeApp.remove();
      unsubscribeNav();
    };
  }, [navigation, step, submitting, attempt]);

  const startAnimations = () => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(badgerBounce, { toValue: -6, duration: 900, useNativeDriver: true }),
        Animated.timing(badgerBounce, { toValue: 0, duration: 900, useNativeDriver: true }),
      ])
    ).start();
  };

  const slideQuestionIn = () => {
    slideAnim.setValue(width);
    Animated.spring(slideAnim, { toValue: 0, friction: 8, tension: 50, useNativeDriver: true }).start();
  };

  const fetchActiveChallenge = async () => {
    try {
      setLoading(true);
      const data = await HoneyPotAPI.getActiveChallenge();
      if (data && data.questions && data.questions.length > 0) setChallenge(data);

      // Fetch User's past stats for the banner
      const history = await HoneyPotAPI.getMyAttempts();
      if (history && Array.isArray(history)) {
        const stats = history.reduce((acc, curr) => {
          if (curr.bundleAwarded) {
            acc.totalWon += parseFloat(curr.bundleRewardAmount || 0);
            acc.count += 1;
          }
          return acc;
        }, { totalWon: 0, count: 0 });
        setPastStats(stats);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStart = async () => {
    try {
      setLoading(true);
      const newAttempt = await HoneyPotAPI.startAttempt(challenge.id);
      setAttempt(newAttempt);
      setTimeLeft(challenge.durationMinutes * 60);
      startTimeRef.current = Date.now();
      setStep('playing');
      slideQuestionIn();
    } catch (err) {
      console.error(err);
      alert('Failed to start challenge. Try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOption = (questionId, option) => {
    const existing = answers.find(a => a.questionId === questionId);
    const newAnswers = existing
      ? answers.map(a => a.questionId === questionId ? { ...a, selectedOption: option } : a)
      : [...answers, { questionId, selectedOption: option }];
    setAnswers(newAnswers);
    setTimeout(() => {
      if (currentQuestionIndex < challenge.questions.length - 1) {
        setCurrentQuestionIndex(prev => prev + 1);
        setCurrentTextAnswer('');
        slideQuestionIn();
      } else {
        handleSubmit(newAnswers);
      }
    }, 400);
  };

  const handleTimeUp = () => { if (step === 'playing') handleSubmit(answers); };

  const handleSubmit = async (submitAnswers = answers) => {
    if (submitting) return;
    try {
      setSubmitting(true);
      const timeTaken = Math.floor((Date.now() - startTimeRef.current) / 1000);
      const resultData = await HoneyPotAPI.submitAttempt(attempt.id, submitAnswers, timeTaken);
      setResults(resultData);
      setStep('results');
    } catch (err) {
      console.error(err);
      alert('Failed to submit answers.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  // ── LOADING ────────────────────────────────────────────────────
  if (loading && !challenge) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: '#FFFBEB' }} edges={['top', 'bottom']}>
        <StatusBar barStyle="dark-content" backgroundColor="#FFFBEB" />
        <View style={styles.centeredFill}>
          <ActivityIndicator size="large" color="#F59E0B" />
          <Caption style={{ marginTop: 8, color: '#B45309' }}>Checking for sweet trivia...</Caption>
        </View>
      </SafeAreaView>
    );
  }

  // ── EMPTY STATE ────────────────────────────────────────────────
  if (!challenge) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: '#FFFBEB' }} edges={['top', 'bottom']}>
        <StatusBar barStyle="dark-content" backgroundColor="#FFFBEB" />
        <LinearGradient colors={['#FFFBEB', '#FEF3C7']} style={{ flex: 1 }}>
          <View style={styles.headerRow}>
            <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn}>
              <Ionicons name="arrow-back" size={20} color="#1F2937" />
            </TouchableOpacity>
          </View>
          <View style={styles.emptyContent}>
            <Animated.Image
              source={MASCOT}
              style={[styles.mascotImg, { transform: [{ translateY: badgerBounce }] }]}
              resizeMode="contain"
            />
            <H2 style={{ marginTop: 12, textAlign: 'center', color: '#78350F' }}>No Active Challenges</H2>
            <Body2 style={{ textAlign: 'center', color: '#92400E', marginTop: 6, marginBottom: 24, paddingHorizontal: 20 }}>
              The badger is resting. Check back soon for a sweet trivia challenge! 🍯
            </Body2>
            <TouchableOpacity style={[styles.primaryBtn, { width: 160 }]} onPress={() => navigation.goBack()}>
              <Text style={styles.btnText}>Go Back</Text>
            </TouchableOpacity>
          </View>
        </LinearGradient>
      </SafeAreaView>
    );
  }

  // ── INTRO ──────────────────────────────────────────────────────
  const renderIntro = () => (
    <ScrollView
      contentContainerStyle={styles.introContainer}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
    >
      <Animated.Image
        source={MASCOT}
        style={[styles.mascotImg, { transform: [{ translateY: badgerBounce }] }]}
        resizeMode="contain"
      />
      <H2 style={styles.titleCompact}>{challenge.title}</H2>

      <View style={styles.pillRow}>
        <View style={styles.pill}>
          <Caption style={styles.pillText}>📚 {challenge.topic}</Caption>
        </View>
        <View style={[styles.pill, {
          backgroundColor: challenge.difficulty === 'hard' ? '#FEE2E2'
            : challenge.difficulty === 'medium' ? '#FEF3C7' : '#D1FAE5'
        }]}>
          <Caption style={[styles.pillText, {
            color: challenge.difficulty === 'hard' ? '#DC2626'
              : challenge.difficulty === 'medium' ? '#D97706' : '#059669'
          }]}>
            ⚡ {challenge.difficulty.toUpperCase()}
          </Caption>
        </View>
      </View>

      <View style={styles.rewardCard}>
        <Text style={{ fontSize: 22 }}>{challenge.rewardType === 'fun' ? '🎮' : '🎁'}</Text>
        <View style={{ marginLeft: 10, flex: 1 }}>
          {challenge.rewardType === 'fun' ? (
            <Body1 style={{ color: '#D97706', fontWeight: '700', letterSpacing: 0.5 }}>
              Chemsha Bongo!
            </Body1>
          ) : (
            <>
              <Caption style={{ color: '#B45309', fontWeight: 'bold', letterSpacing: 0.5 }}>REWARD ON PASS</Caption>
              <Body1 style={{ color: '#1F2937', fontWeight: '700' }}>{challenge.bundleRewardLabel}</Body1>
            </>
          )}
        </View>
      </View>

      <View style={styles.rulesBox}>
        <Caption style={{ fontWeight: '700', color: '#374151', marginBottom: 4 }}>Challenge Details</Caption>
        <Caption style={styles.ruleText}>• {challenge.questions.length} trivia questions</Caption>
        <Caption style={styles.ruleText}>• Pass {challenge.passingScore}%+ to claim reward</Caption>
        <Caption style={styles.ruleText}>• {challenge.durationMinutes} min time limit — no pausing</Caption>
        <Caption style={[styles.ruleText, { color: '#EF4444', fontWeight: 'bold' }]}>• One attempt only.</Caption>
        <Caption style={[styles.ruleText, { color: '#EF4444', fontWeight: 'bold' }]}>• Leaving the quiz auto-disqualifies you!</Caption>
      </View>

      {challenge.userAttempt ? (
        <View style={{ alignItems: 'center', width: '100%', marginTop: 8 }}>
          <View style={[styles.rulesBox, { backgroundColor: '#FEE2E2', borderColor: '#F87171', marginBottom: 12 }]}>
            <Text style={{ color: '#DC2626', fontWeight: '700', textAlign: 'center' }}>You have already completed this challenge.</Text>
          </View>
          <TouchableOpacity
            style={[styles.primaryBtn, { backgroundColor: '#10B981', shadowColor: '#047857' }]}
            onPress={() => {
              setResults({ attempt: challenge.userAttempt, funChallengeUnlocked: false });
              setStep('results');
            }}
          >
            <Text style={styles.btnText}>VIEW PAST RESULTS 🏆</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <TouchableOpacity style={styles.primaryBtn} onPress={handleStart} disabled={loading}>
          {loading
            ? <ActivityIndicator color="white" size="small" />
            : <Text style={styles.btnText}>DIVE IN 🍯</Text>
          }
        </TouchableOpacity>
      )}
    </ScrollView>
  );

  // ── PLAYING (TRIVIA) ───────────────────────────────────────────
  const renderPlaying = () => {
    const question = challenge.questions[currentQuestionIndex];
    if (!question) return null;
    const progressPct = (currentQuestionIndex / challenge.questions.length) * 100;

    return (
      <View style={{ flex: 1 }}>
        {/* Enhanced progress header with walking Mascot */}
        <View style={styles.playHeader}>
          <View style={styles.progressHeaderRow}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <TouchableOpacity onPress={() => navigation.goBack()} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
                <Ionicons name="close" size={24} color="#1F2937" />
              </TouchableOpacity>
              <Caption style={{ marginLeft: 10, fontWeight: '700', color: '#374151', fontSize: 13 }}>
                Q {currentQuestionIndex + 1} / {challenge.questions.length}
              </Caption>
            </View>
            <View style={styles.timerBox}>
              <Ionicons name="time-outline" size={15} color={timeLeft < 30 ? '#EF4444' : '#374151'} />
              <Caption style={{ marginLeft: 4, color: timeLeft < 30 ? '#EF4444' : '#374151', fontWeight: '700', fontSize: 13 }}>
                {formatTime(timeLeft)}
              </Caption>
            </View>
          </View>

          {/* Badger Walking Track */}
          <View style={styles.walkingTrack}>
            <Animated.View style={{
              position: 'absolute',
              bottom: 0,
              left: `${Math.min(progressPct, 85)}%`,
              transform: [{ translateY: badgerBounce }],
              zIndex: 10
            }}>
              <Image source={MASCOT} style={{ width: 42, height: 42 }} resizeMode="contain" />
            </Animated.View>

            <View style={{ position: 'absolute', right: -5, bottom: 2, zIndex: 1 }}>
              <Text style={{ fontSize: 26 }}>🍯</Text>
            </View>
          </View>

          {/* Progress bar */}
          <View style={styles.progressBarBg}>
            <View style={[styles.progressBarFill, { width: `${progressPct}%` }]} />
          </View>
        </View>

        {/* Question + options or Text input */}
        <Animated.View style={[styles.questionCard, { transform: [{ translateX: slideAnim }] }]}>
          <Body1 style={styles.questionText}>{question.questionText}</Body1>
          <ScrollView contentContainerStyle={styles.optionsContainer} showsVerticalScrollIndicator={false}>
            {question.options && question.options.length > 0 ? (
              question.options.map((opt, idx) => {
                const prevAnswer = answers.find(a => a.questionId === question.id);
                const isSelected = prevAnswer?.selectedOption === opt;
                return (
                  <TouchableOpacity
                    key={idx}
                    style={[styles.optionBtn, isSelected && styles.optionSelected]}
                    onPress={() => handleSelectOption(question.id, opt)}
                    activeOpacity={0.75}
                  >
                    <View style={[styles.optionBadge, isSelected && { backgroundColor: '#F59E0B' }]}>
                      <Caption style={{ color: isSelected ? 'white' : '#9CA3AF', fontWeight: '700' }}>
                        {String.fromCharCode(65 + idx)}
                      </Caption>
                    </View>
                    <Text style={[styles.optionText, isSelected && { color: '#B45309', fontWeight: '600' }]}>{opt}</Text>
                    {isSelected && <Ionicons name="checkmark-circle" size={16} color="#F59E0B" />}
                  </TouchableOpacity>
                );
              })
            ) : (
              <View style={styles.textInputArea}>
                <TextInput
                  style={styles.fillBlankInput}
                  placeholder="Type your answer here..."
                  placeholderTextColor="#9CA3AF"
                  value={currentTextAnswer}
                  onChangeText={setCurrentTextAnswer}
                  multiline={false}
                  autoCorrect={false}
                />
                <TouchableOpacity
                  style={[styles.primaryBtn, { marginTop: 18, opacity: currentTextAnswer.trim() ? 1 : 0.5 }]}
                  onPress={() => handleSelectOption(question.id, currentTextAnswer.trim())}
                  disabled={!currentTextAnswer.trim()}
                >
                  <Text style={styles.btnText}>Submit Answer</Text>
                </TouchableOpacity>
              </View>
            )}
          </ScrollView>
        </Animated.View>

        {submitting && (
          <View style={styles.submittingOverlay}>
            <ActivityIndicator size="large" color="#F59E0B" />
            <Body2 style={{ marginTop: 8, color: '#B45309' }}>Scoring your answers...</Body2>
          </View>
        )}
      </View>
    );
  };

  // ── RESULTS ────────────────────────────────────────────────────
  const renderResults = () => {
    const { attempt: resAttempt } = results;
    return (
      <ScrollView contentContainerStyle={styles.resultsContainer} showsVerticalScrollIndicator={false}>
        {resAttempt.passed && (
          <ConfettiCannon count={120} origin={{ x: width / 2, y: 0 }} autoStart fallSpeed={2500} />
        )}

        <View style={{ alignItems: 'center', paddingTop: 8 }}>
          <Text style={{ fontSize: 44 }}>{resAttempt.passed ? '🎉🍯' : '😿🥀'}</Text>
          <H2 style={[styles.titleCompact, { color: resAttempt.passed ? '#059669' : '#DC2626', marginTop: 4 }]}>
            {resAttempt.passed ? 'Sweet Victory!' : 'Not Quite There...'}
          </H2>
          <Body2 style={{ color: '#374151' }}>
            You scored <Text style={{ fontWeight: '700' }}>{resAttempt.score}%</Text>
          </Body2>
          <Caption style={{ color: '#9CA3AF' }}>Passing score: {challenge.passingScore}%</Caption>
        </View>

        {resAttempt.passed && resAttempt.bundleAwarded && challenge.rewardType === 'fun' && (
          <View style={[styles.rewardWonCard, { backgroundColor: '#7C3AED' }]}>
            <Text style={{ fontSize: 24 }}>🎮</Text>
            <View style={{ marginLeft: 10, flex: 1 }}>
              <Caption style={{ color: '#E9D5FF', fontWeight: '700', letterSpacing: 0.5 }}>FUN BONUS UNLOCKED!</Caption>
              <Body1 style={{ color: 'white', fontWeight: '700' }}>Mini Trivia Game</Body1>
            </View>
          </View>
        )}

        {resAttempt.passed && resAttempt.bundleAwarded && challenge.rewardType !== 'fun' && (
          <View style={styles.rewardWonCard}>
            <Text style={{ fontSize: 24 }}>
              {challenge.rewardType === 'points' ? '✨' : challenge.rewardType === 'cash' ? '💵' : '🎁'}
            </Text>
            <View style={{ marginLeft: 10, flex: 1 }}>
              <Caption style={{ color: 'white', fontWeight: '700', letterSpacing: 0.5 }}>REWARD UNLOCKED</Caption>
              <Body1 style={{ color: 'white', fontWeight: '700' }}>
                {challenge.rewardType === 'points'
                  ? `${challenge.bundleRewardAmount} Points`
                  : challenge.bundleRewardLabel}
              </Body1>
            </View>
          </View>
        )}

        {resAttempt.passed && results.funChallengeUnlocked && (
          <TouchableOpacity
            style={[styles.primaryBtn, { backgroundColor: '#7C3AED', shadowColor: '#5B21B6', marginBottom: 16 }]}
            onPress={() => {
              Alert.alert('🎮 Bonus Game', 'Entering rapid mini trivia round... (Game engine launching!)');
            }}
          >
            <Text style={styles.btnText}>Play Fun Challenge Now</Text>
          </TouchableOpacity>
        )}

        <View style={styles.answersReview}>
          <Body1 style={{ fontWeight: '600', marginBottom: 8, color: '#374151' }}>Answer Review</Body1>
          {resAttempt.answers.map((ans, idx) => (
            <View key={idx} style={[styles.reviewItem, ans.isCorrect ? styles.reviewCorrect : styles.reviewWrong]}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 }}>
                <Caption style={{ fontWeight: '700', color: '#374151' }}>Q{idx + 1}</Caption>
                <Ionicons name={ans.isCorrect ? 'checkmark-circle' : 'close-circle'} size={15} color={ans.isCorrect ? '#059669' : '#DC2626'} />
              </View>
              <Caption>Your answer: <Text style={{ fontWeight: '600' }}>{ans.selectedOption || 'Skipped'}</Text></Caption>
              {!ans.isCorrect && <Caption style={{ color: '#DC2626' }}>Correct: {ans.correctAnswer}</Caption>}
              {ans.explanation && <Caption style={{ marginTop: 4, fontStyle: 'italic', color: '#6B7280' }}>"{ans.explanation}"</Caption>}
              {ans.funFact && (
                <View style={styles.funFactBox}>
                  <Caption style={{ color: '#7C3AED', fontWeight: '700' }}>✨ Fun Fact</Caption>
                  <Caption style={{ color: '#5B21B6' }}>{ans.funFact}</Caption>
                </View>
              )}
            </View>
          ))}
        </View>

        <TouchableOpacity style={[styles.primaryBtn, { marginBottom: 28 }]} onPress={() => navigation.navigate('Dashboard')}>
          <Text style={styles.btnText}>Back to Dashboard</Text>
        </TouchableOpacity>
      </ScrollView>
    );
  };

  // ── FOOTER BANNER ──────────────────────────────────────────────
  const renderFooterBanner = () => {
    let content = "";
    let icon = "bulb-outline";

    if (step === 'playing') {
      const currentQ = challenge.questions[currentQuestionIndex];
      content = currentQ?.funFact ? `TIP: ${currentQ.funFact}` : "🍯 Tip: Be sure to read the questions carefully!";
    } else {
      if (pastStats.count > 0) {
        if (pastStats.totalWon > 0) {
          content = `Congrats! You've won ${pastStats.totalWon}MB total from ${pastStats.count} challenges! 🏆`;
        } else {
          content = `Awesome! You've tackled ${pastStats.count} challenge${pastStats.count > 1 ? 's' : ''} so far. Keep going! 🏆`;
        }
        icon = "trophy-outline";
      } else {
        content = "Win your first Honey Pot challenge today for sweet rewards! ✨";
      }
    }

    return (
      <View style={styles.footerBanner}>
        <LinearGradient
          colors={['#FEF3C7', '#FDE68A']}
          start={{ x: 0, y: 0 }} end={{ x: 0, y: 1 }}
          style={styles.footerGradient}
        >
          <Ionicons name={icon} size={18} color="#D97706" style={{ marginRight: 8 }} />
          <Caption style={styles.footerText}>
            {content}
          </Caption>
        </LinearGradient>
      </View>
    );
  };

  // ── MAIN ───────────────────────────────────────────────────────
  return (
    // SafeAreaView is THE outermost container so status bar items are never overlaid
    <SafeAreaView style={{ flex: 1, backgroundColor: '#FFFBEB' }} edges={['top', 'bottom']}>
      <StatusBar barStyle="dark-content" backgroundColor="#FFFBEB" />
      <LinearGradient colors={['#FFFBEB', '#FEF3C7']} style={{ flex: 1 }}>

        {step === 'intro' && (
          <View style={{ flex: 1 }}>
            <View style={styles.headerRow}>
              <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn}>
                <Ionicons name="arrow-back" size={20} color="#1F2937" />
              </TouchableOpacity>
            </View>
            {renderIntro()}
          </View>
        )}

        {step === 'playing' && (
          <View style={{ flex: 1 }}>
            {renderPlaying()}
          </View>
        )}

        {step === 'results' && (
          <View style={{ flex: 1 }}>
            <View style={styles.headerRow}>
              <TouchableOpacity onPress={() => navigation.navigate('Dashboard')} style={styles.backBtn}>
                <Ionicons name="arrow-back" size={20} color="#1F2937" />
              </TouchableOpacity>
            </View>
            {renderResults()}
          </View>
        )}

        {renderFooterBanner()}
      </LinearGradient>
    </SafeAreaView>
  );
};

// ── STYLES ─────────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  centeredFill: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  emptyContent: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingHorizontal: 32 },
  headerRow: { paddingHorizontal: 16, paddingTop: 6, paddingBottom: 2 },
  backBtn: { width: 34, height: 34, borderRadius: 17, backgroundColor: 'white', justifyContent: 'center', alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.1, shadowRadius: 3, elevation: 2 },

  mascotImg: { width: 130, height: 130, alignSelf: 'center', marginTop: 4, marginBottom: 2 },

  introContainer: { paddingHorizontal: 16, paddingBottom: 20, alignItems: 'center' },
  titleCompact: { textAlign: 'center', color: '#1F2937', marginVertical: 6 },
  pillRow: { flexDirection: 'row', gap: 8, marginBottom: 12, flexWrap: 'wrap', justifyContent: 'center' },
  pill: { backgroundColor: '#FDE68A', paddingHorizontal: 10, paddingVertical: 4, borderRadius: 20 },
  pillText: { color: '#92400E', fontWeight: '700' },
  rewardCard: { flexDirection: 'row', backgroundColor: 'white', padding: 12, borderRadius: 12, width: '100%', alignItems: 'center', shadowColor: '#D97706', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.12, shadowRadius: 5, elevation: 3, marginBottom: 10 },
  rulesBox: { backgroundColor: 'rgba(255,255,255,0.55)', padding: 12, borderRadius: 10, width: '100%', marginBottom: 14 },
  ruleText: { color: '#4B5563', marginBottom: 2 },
  primaryBtn: { backgroundColor: '#F59E0B', width: '100%', paddingVertical: 13, borderRadius: 12, alignItems: 'center', shadowColor: '#B45309', shadowOffset: { width: 0, height: 3 }, shadowOpacity: 0.22, shadowRadius: 5, elevation: 4 },
  btnText: { color: 'white', fontSize: 14, fontWeight: '700', letterSpacing: 0.3 },

  // Trivia / playing
  playHeader: { paddingHorizontal: 18, paddingTop: 12, paddingBottom: 22 },
  progressHeaderRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 },
  timerBox: { flexDirection: 'row', alignItems: 'center', backgroundColor: 'white', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 12 },
  walkingTrack: { width: '100%', height: 46, marginBottom: 8, position: 'relative' },
  progressBarBg: { height: 6, backgroundColor: '#FDE68A', borderRadius: 3, overflow: 'hidden' },
  progressBarFill: { height: '100%', backgroundColor: '#F59E0B' },

  questionCard: { flex: 1, backgroundColor: 'white', borderTopLeftRadius: 22, borderTopRightRadius: 22, padding: 18, shadowColor: '#000', shadowOffset: { width: 0, height: -2 }, shadowOpacity: 0.06, shadowRadius: 6, elevation: 6 },
  questionText: { color: '#111827', marginBottom: 16, lineHeight: 24, fontWeight: '600' },
  optionsContainer: { gap: 8, paddingBottom: 24 },
  optionBtn: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#F9FAFB', borderWidth: 1.5, borderColor: '#F3F4F6', paddingVertical: 11, paddingHorizontal: 12, borderRadius: 10 },
  optionSelected: { backgroundColor: '#FFFBEB', borderColor: '#FCD34D' },
  optionBadge: { width: 24, height: 24, borderRadius: 12, backgroundColor: '#F3F4F6', justifyContent: 'center', alignItems: 'center', marginRight: 10 },
  optionText: { fontSize: 13, color: '#374151', flex: 1 },
  textInputArea: { width: '100%', marginTop: 8 },
  fillBlankInput: { backgroundColor: '#F9FAFB', borderWidth: 1.5, borderColor: '#E5E7EB', borderRadius: 10, paddingHorizontal: 16, paddingVertical: 14, fontSize: 15, color: '#1F2937', fontWeight: '500' },
  submittingOverlay: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(255,251,235,0.93)', justifyContent: 'center', alignItems: 'center' },

  // Results
  resultsContainer: { padding: 16, paddingTop: 4 },
  rewardWonCard: { flexDirection: 'row', backgroundColor: '#F59E0B', padding: 14, borderRadius: 12, alignItems: 'center', marginTop: 12, shadowColor: '#D97706', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.22, shadowRadius: 5, elevation: 4 },
  answersReview: { marginTop: 18, marginBottom: 10 },
  reviewItem: { backgroundColor: 'white', padding: 10, borderRadius: 10, marginBottom: 8, borderWidth: 1 },
  reviewCorrect: { borderColor: '#10B981', backgroundColor: '#ECFDF5' },
  reviewWrong: { borderColor: '#EF4444', backgroundColor: '#FEF2F2' },
  funFactBox: { marginTop: 8, padding: 8, backgroundColor: 'rgba(245, 158, 11, 0.08)', borderRadius: 8, borderLeftWidth: 3, borderLeftColor: '#F59E0B' },
  footerBanner: { position: 'absolute', bottom: 0, left: 0, right: 0, height: '15%' },
  footerGradient: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingHorizontal: 20, borderTopWidth: 1, borderTopColor: '#FCD34D' },
  footerText: { color: '#92400E', fontWeight: '600', fontSize: 13, flex: 1, textAlign: 'center', lineHeight: 18 }
});

export default HoneyPotScreen;
